import { type GisMarker } from "@/mocks/gis";

interface Props {
  marker: GisMarker;
  onClose: () => void;
}

const typeLabels: Record<string, string> = {
  resident: "居民用户",
  commercial: "工商业用户",
  alarm: "告警点位",
  pipeline: "管网节点",
  station: "调压站",
};

const alarmLevelColors: Record<string, string> = {
  "紧急": "bg-red-100 text-red-700 border-red-200",
  "严重": "bg-orange-100 text-orange-700 border-orange-200",
  "警告": "bg-yellow-100 text-yellow-700 border-yellow-200",
  "提示": "bg-blue-100 text-blue-700 border-blue-200",
};

export default function MarkerPopup({ marker, onClose }: Props) {
  return (
    <div className="absolute top-4 right-4 w-72 bg-white rounded-xl border border-gray-200 z-[1000] overflow-hidden">
      {/* 头部 */}
      <div className={`px-4 py-3 flex items-center justify-between ${
        marker.type === "alarm" ? "bg-red-50 border-b border-red-100" :
        marker.type === "station" ? "bg-blue-50 border-b border-blue-100" :
        marker.type === "commercial" ? "bg-orange-50 border-b border-orange-100" :
        "bg-gray-50 border-b border-gray-100"
      }`}>
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
            marker.type === "alarm" ? "bg-red-100" :
            marker.type === "station" ? "bg-blue-100" :
            marker.type === "commercial" ? "bg-orange-100" :
            "bg-gray-100"
          }`}>
            <i className={`text-sm ${
              marker.type === "alarm" ? "ri-alarm-warning-line text-red-500" :
              marker.type === "station" ? "ri-building-4-line text-blue-600" :
              marker.type === "commercial" ? "ri-building-2-line text-orange-600" :
              "ri-home-4-line text-gray-600"
            }`}></i>
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-800">{marker.name}</div>
            <div className="text-xs text-gray-400">{typeLabels[marker.type]}</div>
          </div>
        </div>
        <button onClick={onClose} className="w-6 h-6 flex items-center justify-center hover:bg-white/80 rounded-full cursor-pointer">
          <i className="ri-close-line text-gray-400 text-sm"></i>
        </button>
      </div>

      {/* 内容 */}
      <div className="p-4 space-y-2.5">
        <div className="flex items-start gap-2">
          <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
            <i className="ri-map-pin-line text-xs text-gray-400"></i>
          </div>
          <span className="text-xs text-gray-500 leading-relaxed">{marker.address}</span>
        </div>

        {/* 状态 */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">状态</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
            marker.status === "正常" ? "bg-green-50 text-green-700 border-green-200" :
            marker.status === "未处理" ? "bg-red-50 text-red-700 border-red-200" :
            marker.status === "处理中" ? "bg-orange-50 text-orange-700 border-orange-200" :
            "bg-yellow-50 text-yellow-700 border-yellow-200"
          }`}>
            {marker.status}
          </span>
          {marker.alarmLevel && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${alarmLevelColors[marker.alarmLevel] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
              {marker.alarmLevel}
            </span>
          )}
        </div>

        {/* 数据指标 */}
        <div className="grid grid-cols-2 gap-2">
          {marker.balance !== undefined && (
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="text-xs text-gray-400">账户余额</div>
              <div className="text-sm font-semibold text-gray-800">¥{marker.balance.toFixed(2)}</div>
            </div>
          )}
          {marker.monthlyUsage !== undefined && (
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="text-xs text-gray-400">月用气量</div>
              <div className="text-sm font-semibold text-gray-800">{marker.monthlyUsage.toLocaleString()} m³</div>
            </div>
          )}
          {marker.deviceCount !== undefined && (
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="text-xs text-gray-400">设备数量</div>
              <div className="text-sm font-semibold text-gray-800">{marker.deviceCount} 台</div>
            </div>
          )}
          {marker.lastUpdate && (
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="text-xs text-gray-400">最后更新</div>
              <div className="text-xs font-medium text-gray-700">{marker.lastUpdate.split(" ")[1]}</div>
            </div>
          )}
        </div>

        {/* 坐标 */}
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <i className="ri-crosshair-line"></i>
          <span>{marker.lat.toFixed(4)}°N, {marker.lng.toFixed(4)}°E</span>
        </div>
      </div>
    </div>
  );
}
